import React, { useState, useEffect } from 'react';
import api from '../api';

const CATEGORIES = [
  { key: 'all', label: 'All', icon: '◈' },
  { key: 'tech', label: 'Tech', icon: '⬡' },
  { key: 'news', label: 'News', icon: '◎' },
  { key: 'science', label: 'Science', icon: '◉' },
  { key: 'business', label: 'Business', icon: '◈' },
  { key: 'crypto', label: 'Crypto', icon: '◇' },
  { key: 'fitness', label: 'Fitness', icon: '◆' },
  { key: 'politics', label: 'Politics', icon: '◑' },
  { key: 'independent', label: 'Independent', icon: '◐' },
  { key: 'culture', label: 'Culture', icon: '◒' },
];

const DiscoverScreen = ({ user }) => {
  const [sources, setSources] = useState([]);
  const [category, setCategory] = useState('all');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [addingId, setAddingId] = useState(null);
  const [votingId, setVotingId] = useState(null);
  const [addedIds, setAddedIds] = useState([]);
  const [showSubmit, setShowSubmit] = useState(false);
  const [submitUrl, setSubmitUrl] = useState('');
  const [submitName, setSubmitName] = useState('');
  const [submitCategory, setSubmitCategory] = useState('independent');
  const [submitting, setSubmitting] = useState(false);
  const [submitSuccess, setSubmitSuccess] = useState('');

  useEffect(() => {
    fetchSources();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [category]);

  const fetchSources = async () => {
    setLoading(true);
    try {
      const res = await api.get(`/featured_sources?category=${category}`);
      setSources(res.data);
    } catch (err) {
      setError('Failed to load sources.');
    } finally {
      setLoading(false);
    }
  };

  const handleAddToMySources = async (source) => {
    setAddingId(source.id);
    try {
      await api.post('/sources', { url: source.url, name: source.name });
      setAddedIds([...addedIds, source.id]);
    } catch (err) {
      const msg = err.response?.data?.error || 'Failed to add source.';
      setError(msg);
      setTimeout(() => setError(''), 3000);
    } finally {
      setAddingId(null);
    }
  };

  const handleVote = async (source) => {
    setVotingId(source.id);
    try {
      const res = await api.post(`/featured_sources/${source.id}/upvote`);
      setSources(sources.map(s =>
        s.id === source.id
          ? { ...s, upvote_count: res.data.upvote_count, voted: res.data.voted }
          : s
      ));
    } catch (err) {
      setError('Failed to vote.');
      setTimeout(() => setError(''), 3000);
    } finally {
      setVotingId(null);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setError('');
    try {
      const res = await api.post('/featured_sources/submit', {
        url: submitUrl,
        name: submitName,
        category: submitCategory,
      });
      setSources([res.data, ...sources]);
      setSubmitUrl('');
      setSubmitName('');
      setShowSubmit(false);
      setSubmitSuccess('Feed submitted successfully!');
      setTimeout(() => setSubmitSuccess(''), 4000);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to submit feed.');
    } finally {
      setSubmitting(false);
    }
  };

  const getCategoryColor = (cat) => {
    const colors = {
      tech: '#00d4ff',
      news: '#7b61ff',
      science: '#00ffaa',
      business: '#ffd700',
      crypto: '#ff9500',
      fitness: '#ff4d6d',
      politics: '#a78bfa',
      independent: '#64748b',
      culture: '#f472b6',
    };
    return colors[cat] || '#6b7280';
  };

  return (
    <div style={styles.page}>
      <div style={styles.container}>

        {/* Header */}
        <div style={styles.header}>
          <div>
            <h1 style={styles.title}>Discover</h1>
            <p style={styles.subtitle}>
              Community-curated RSS feeds. Vote up your favorites, add them to your brief.
            </p>
          </div>
          <button
            className="btn btn-primary"
            style={{ fontSize: 13, padding: '10px 18px', flexShrink: 0 }}
            onClick={() => setShowSubmit(!showSubmit)}
          >
            {showSubmit ? '✕ Cancel' : '+ Submit Feed'}
          </button>
        </div>

        {/* Submit form */}
        {showSubmit && (
          <div style={styles.submitCard} className="fade-in">
            <div style={styles.submitHeader}>Submit a Feed</div>
            <form onSubmit={handleSubmit} style={styles.submitForm}>
              <div style={styles.submitRow}>
                <div style={{ flex: 2 }}>
                  <label style={styles.label}>Feed URL</label>
                  <input
                    type="url"
                    value={submitUrl}
                    onChange={(e) => setSubmitUrl(e.target.value)}
                    placeholder="https://example.com/feed.xml"
                    required
                  />
                </div>
                <div style={{ flex: 1 }}>
                  <label style={styles.label}>Name</label>
                  <input
                    type="text"
                    value={submitName}
                    onChange={(e) => setSubmitName(e.target.value)}
                    placeholder="Feed name"
                  />
                </div>
                <div style={{ flex: 1 }}>
                  <label style={styles.label}>Category</label>
                  <select
                    value={submitCategory}
                    onChange={(e) => setSubmitCategory(e.target.value)}
                    style={styles.select}
                  >
                    {CATEGORIES.filter(c => c.key !== 'all').map(c => (
                      <option key={c.key} value={c.key}>{c.label}</option>
                    ))}
                  </select>
                </div>
              </div>
              <button
                type="submit"
                className="btn btn-primary"
                disabled={submitting}
                style={{ alignSelf: 'flex-start' }}
              >
                {submitting ? 'Submitting...' : 'Submit Feed'}
              </button>
            </form>
          </div>
        )}

        {submitSuccess && (
          <div style={styles.successMsg}>{submitSuccess}</div>
        )}

        {error && (
          <div style={styles.errorMsg}>{error}</div>
        )}

        {/* Category tabs */}
        <div style={styles.categoryScroll}>
          <div style={styles.categories}>
            {CATEGORIES.map((cat) => (
              <button
                key={cat.key}
                style={{
                  ...styles.catBtn,
                  ...(category === cat.key ? styles.catBtnActive : {}),
                }}
                onClick={() => setCategory(cat.key)}
              >
                {cat.label}
              </button>
            ))}
          </div>
        </div>

        {/* Sources count */}
        <div style={styles.countRow}>
          <span style={styles.countText}>
            {loading ? '...' : `${sources.length} feeds`}
          </span>
          <span style={styles.countHint}>Sorted by community votes</span>
        </div>

        {/* Loading skeletons */}
        {loading && (
          <div style={styles.list}>
            {[1, 2, 3, 4, 5].map((i) => (
              <div key={i} className="skeleton" style={styles.skeletonItem} />
            ))}
          </div>
        )}

        {/* Sources list */}
        {!loading && sources.length === 0 && (
          <div style={styles.empty}>
            <div style={styles.emptyIcon}>◉</div>
            <div style={styles.emptyTitle}>No feeds in this category yet</div>
            <div style={styles.emptySub}>Be the first to submit one!</div>
          </div>
        )}

        {!loading && sources.length > 0 && (
          <div style={styles.list}>
            {sources.map((source, i) => (
              <div
                key={source.id}
                style={{ ...styles.sourceCard, animationDelay: `${i * 0.03}s` }}
                className="fade-in"
              >
                {/* Vote button */}
                <button
                  style={{
                    ...styles.voteBtn,
                    ...(source.voted ? styles.voteBtnActive : {}),
                  }}
                  onClick={() => handleVote(source)}
                  disabled={votingId === source.id}
                >
                  <span style={styles.voteArrow}>▲</span>
                  <span style={styles.voteCount}>{source.upvote_count}</span>
                </button>

                {/* Source info */}
                <div style={styles.sourceInfo}>
                  <div style={styles.sourceTop}>
                    <span style={styles.sourceName}>{source.name}</span>
                    <span
                      style={{
                        ...styles.catTag,
                        background: `${getCategoryColor(source.category)}18`,
                        color: getCategoryColor(source.category),
                        border: `1px solid ${getCategoryColor(source.category)}30`,
                      }}
                    >
                      {source.category}
                    </span>
                  </div>
                  <div style={styles.sourceDesc}>{source.description}</div>
                  <div style={styles.sourceUrl}>{source.url}</div>
                </div>

                {/* Add button */}
                <button
                  className={addedIds.includes(source.id) ? 'btn btn-ghost' : 'btn btn-primary'}
                  style={styles.addBtn}
                  onClick={() => !addedIds.includes(source.id) && handleAddToMySources(source)}
                  disabled={addingId === source.id || addedIds.includes(source.id)}
                >
                  {addingId === source.id
                    ? '...'
                    : addedIds.includes(source.id)
                    ? '✓ Added'
                    : '+ Add'}
                </button>
              </div>
            ))}
          </div>
        )}

      </div>
    </div>
  );
};

const styles = {
  page: {
    minHeight: '100vh',
    padding: '40px 24px',
  },
  container: {
    maxWidth: 860,
    margin: '0 auto',
    display: 'flex',
    flexDirection: 'column',
    gap: 20,
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    flexWrap: 'wrap',
    gap: 16,
  },
  title: {
    fontSize: 28,
    fontWeight: 700,
    color: 'var(--text)',
    letterSpacing: '-0.02em',
  },
  subtitle: {
    fontSize: 14,
    color: 'var(--text-secondary)',
    marginTop: 6,
  },
  submitCard: {
    background: 'var(--surface)',
    border: '1px solid var(--primary)',
    borderRadius: 16,
    padding: 24,
  },
  submitHeader: {
    fontSize: 15,
    fontWeight: 600,
    color: 'var(--text)',
    marginBottom: 16,
  },
  submitForm: {
    display: 'flex',
    flexDirection: 'column',
    gap: 16,
  },
  submitRow: {
    display: 'flex',
    gap: 16,
    flexWrap: 'wrap',
  },
  label: {
    display: 'block',
    fontSize: 11,
    fontWeight: 600,
    color: 'var(--text-secondary)',
    letterSpacing: '0.06em',
    textTransform: 'uppercase',
    marginBottom: 6,
  },
  select: {
    fontFamily: 'Space Grotesk, sans-serif',
    background: 'var(--surface2)',
    border: '1px solid var(--border)',
    borderRadius: '8px',
    color: 'var(--text)',
    padding: '12px 16px',
    fontSize: '14px',
    width: '100%',
    outline: 'none',
  },
  successMsg: {
    background: 'rgba(0, 255, 170, 0.08)',
    border: '1px solid rgba(0, 255, 170, 0.25)',
    borderRadius: 10,
    padding: '12px 16px',
    fontSize: 13,
    color: 'var(--success)',
  },
  errorMsg: {
    background: 'rgba(255, 77, 109, 0.1)',
    border: '1px solid rgba(255, 77, 109, 0.3)',
    borderRadius: 10,
    padding: '12px 16px',
    fontSize: 13,
    color: 'var(--danger)',
  },
  categoryScroll: {
    overflowX: 'auto',
    WebkitOverflowScrolling: 'touch',
    marginLeft: -24,
    marginRight: -24,
    paddingLeft: 24,
    paddingRight: 24,
  },
  categories: {
    display: 'flex',
    gap: 8,
    paddingBottom: 4,
    width: 'max-content',
  },
  catBtn: {
    padding: '8px 16px',
    borderRadius: 20,
    border: '1px solid var(--border)',
    background: 'transparent',
    color: 'var(--text-secondary)',
    fontSize: 13,
    fontWeight: 500,
    fontFamily: 'Space Grotesk, sans-serif',
    cursor: 'pointer',
    whiteSpace: 'nowrap',
    transition: 'all 0.2s',
  },
  catBtnActive: {
    background: 'var(--primary-dim)',
    borderColor: 'var(--primary)',
    color: 'var(--primary)',
  },
  countRow: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  countText: {
    fontSize: 13,
    fontWeight: 600,
    color: 'var(--text-secondary)',
    letterSpacing: '0.04em',
  },
  countHint: {
    fontSize: 11,
    color: 'var(--text-dim)',
  },
  list: {
    display: 'flex',
    flexDirection: 'column',
    gap: 10,
  },
  skeletonItem: {
    height: 80,
    borderRadius: 14,
  },
  empty: {
    textAlign: 'center',
    padding: '48px 24px',
    background: 'var(--surface)',
    border: '1px solid var(--border)',
    borderRadius: 16,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: 10,
  },
  emptyIcon: {
    fontSize: 36,
    color: 'var(--text-dim)',
  },
  emptyTitle: {
    fontSize: 16,
    fontWeight: 600,
    color: 'var(--text-secondary)',
  },
  emptySub: {
    fontSize: 13,
    color: 'var(--text-dim)',
  },
  sourceCard: {
    display: 'flex',
    alignItems: 'center',
    gap: 16,
    background: 'var(--surface)',
    border: '1px solid var(--border)',
    borderRadius: 14,
    padding: '14px 16px',
    transition: 'border-color 0.2s',
  },
  voteBtn: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: 2,
    padding: '8px 10px',
    borderRadius: 8,
    border: '1px solid var(--border)',
    background: 'transparent',
    color: 'var(--text-secondary)',
    cursor: 'pointer',
    flexShrink: 0,
    transition: 'all 0.2s',
    minWidth: 44,
  },
  voteBtnActive: {
    background: 'var(--primary-dim)',
    borderColor: 'var(--primary)',
    color: 'var(--primary)',
  },
  voteArrow: {
    fontSize: 10,
  },
  voteCount: {
    fontSize: 13,
    fontWeight: 700,
    lineHeight: 1,
  },
  sourceInfo: {
    flex: 1,
    overflow: 'hidden',
  },
  sourceTop: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    marginBottom: 4,
    flexWrap: 'wrap',
  },
  sourceName: {
    fontSize: 14,
    fontWeight: 600,
    color: 'var(--text)',
  },
  catTag: {
    fontSize: 10,
    fontWeight: 600,
    letterSpacing: '0.08em',
    padding: '2px 7px',
    borderRadius: 10,
    textTransform: 'uppercase',
  },
  sourceDesc: {
    fontSize: 12,
    color: 'var(--text-secondary)',
    marginBottom: 3,
    lineHeight: 1.5,
  },
  sourceUrl: {
    fontSize: 11,
    color: 'var(--text-dim)',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  addBtn: {
    fontSize: 12,
    padding: '8px 14px',
    flexShrink: 0,
    minWidth: 70,
  },
};

export default DiscoverScreen;
